import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import FunnelStepTypes from "../../pages/funnel-step/types";
import { useTranslation } from "react-i18next";
import { PLAN_TYPE } from "../../utils/constants";

const AccordionContainer = ({
  accordions,
  defaultActiveAccordionId,
  onAccordionClick,
  personalityType,
  personalityName,
  personalityGroup,
  assessmentAttemptId,
  handleContinue,
  skuId,
}) => {
  const dispatch = useDispatch();
  const [activeAccordion, setActiveAccordion] = useState(null);
  const selectedPlan = useSelector((state) => state.funnelStep.selectedPlan);
  const { t } = useTranslation();

  useEffect(() => {
    if (!activeAccordion && accordions.length > 0) {
      const defaultPlan =
        accordions.find((acc) => acc.id === defaultActiveAccordionId) ||
        accordions[1];
      setActiveAccordion(defaultPlan.id);
      dispatch({
        type: FunnelStepTypes.SET_SELECTED_PLAN,
        selectedPlan: defaultPlan,
      });
    }
  }, [accordions, dispatch, activeAccordion, defaultActiveAccordionId]);

  const handleAccordionClick = (accordion) => {
    setActiveAccordion(accordion.id);
    dispatch({
      type: FunnelStepTypes.SET_SELECTED_PLAN,
      selectedPlan: accordion,
    });
    onAccordionClick(accordion);
  };

  return (
    <div className="flex flex-col gap-[10px] lg:gap-[20px]">
      <div className="flex flex-col gap-2">
        {accordions?.map((accordion, index) => (
          <Accordion
            key={accordion.id}
            item={accordion}
            index={index}
            isActive={activeAccordion === accordion.id}
            onClick={() => handleAccordionClick(accordion)}
            personalityType={personalityType}
            personalityName={personalityName}
            skuId={skuId}
            personalityGroup={personalityGroup}
            assessmentAttemptId={assessmentAttemptId}
            t={t}
            handleContinue={handleContinue}
          />
        ))}
      </div>
      <AccordionContent activeAccordion={selectedPlan || accordions[1]} t={t} />
    </div>
  );
};
const Accordion = ({
  item,
  index,
  isActive,
  onClick,
  personalityName,
  personalityType,
  skuId,
  personalityGroup,
  assessmentAttemptId,
  t,
  handleContinue,
}) => {
  const { title, price, scheduleTypeCD } = item;
  const navigate = useNavigate();
  const [lastTapTime, setLastTapTime] = useState(0);

  const handleDoubleTap = () => {
    const currentTime = new Date().getTime();
    const timeDifference = currentTime - lastTapTime;

    // If the second tap is within 300ms (you can adjust this time), navigate to the route
    if (timeDifference < 300 && timeDifference > 0) {
      handleContinue();
    }

    // Update the last tap time
    setLastTapTime(currentTime);
  };

  return (
    <button
      id={`plan-option-button-${index}`}
      type="button"
      className={`w-full text-left px-4 py-2 relative ${
        isActive
          ? "bg-white border-2 border-[#805AED] outline-none ring-2 ring-[#805AED]"
          : "bg-gray-100 hover:bg-gray-200 border border-gray-300 text-insightful-gray"
      } rounded-lg flex items-center`}
      onClick={onClick}
      onTouchStart={handleDoubleTap}
      onDoubleClick={() => {
        handleContinue();
      }}
    >
      <div
        className={`w-[26px] h-6  mr-3 rounded-full border ${
          isActive
            ? "bg-insightful-purple border-insightful-purple"
            : "border-insightful-light-gray"
        } flex items-center justify-center`}
      >
        {isActive && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-white p-[1px]"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>
      <div className="flex justify-between items-center text-[16px] md:text-[20px] lg:text-[24px] w-full overflow-hidden">
        <span className="font-semibold">{title}</span>
        <span className="font-semibold">
          {price}
          {title?.toUpperCase() === PLAN_TYPE.ANNUAL_PLAN && (
            <span className="text-[16px] font-normal"> {t("plan.month")}</span>
          )}
        </span>
        {title?.toUpperCase() === PLAN_TYPE.FOURTEEN_DAYS_FULL_ACCESS && (
          <div className="absolute lg:top-4 right-[50px] lg:right-[70px] -rotate-[45deg] lg:max-w-[110px] overflow-hidden">
            <div
              style={{
                clipPath:
                  "polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))",
              }}
              className={`text-white text-[8px] px-[11px] md:px-[15px] lg:px-[19px] py-1 text-center font-bold ${
                isActive ? "bg-[#FE6634]" : "bg-insightful-light-gray"
              } flex items-center justify-center`}
            >
              {t("plan.most_popular")}
            </div>
          </div>
        )}
      </div>
    </button>
  );
};

const AccordionContent = ({ activeAccordion, t }) => {
  if (Object.keys(activeAccordion).length == 0) return null;
  const policy = activeAccordion.policy;
  const supportText = t("plan.cancel_email");
  const supportEmail = t("plan.support_email");
  const cancelMethods = t("plan.chat_by_online");

  // Replace `YYYY` dynamically & remove `$$`
  const updatedText = policy
    .replace(
      "YYYY",
      `${supportText} <a href="mailto:${supportEmail}" class="text-[#805AED] underline">${supportEmail}</a> ${cancelMethods}`
    )
    .replace(/\$\$/g, "$");

  return (
    <div className="md:mt-auto pb-[2px] min-h-max">
      <div className="flex flex-col gap-2 lg:gap-4 mb-[10px]">
        {activeAccordion.content.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <img src={item.image} alt={item.title} className="w-5 h-5" />
            <span className="text-[#444C5E] font-medium text-[16px] lg:text-[20px]">
              {item.description}
            </span>
          </div>
        ))}
      </div>
      <div className="text-[16px] text-[#444C5E] text-center w-full">
        <div className="border-b mb-[10px] w-full"></div>
        <div
          className="w-full px-2 text-center text-[16px] text-[#444C5E] leading-relaxed break-words"
          dangerouslySetInnerHTML={{ __html: updatedText }}
        ></div>
      </div>
    </div>
  );
};

export default AccordionContainer;
